/**
 * Mirror of sreg-api `common/permission/permission.go` (Table). Gate UI on
 * capabilities, never on role strings. Both sides assert the same written-out
 * matrix in their tests (capabilities.test.ts / permission_test.go) — change
 * them together.
 *
 * Strawman matrix pending Austin's review (Backlog Q6).
 */

export const ROLES = ['principal', 'principal_admin', 'controller', 'property_manager', 'devops_admin'] as const;
export type Role = (typeof ROLES)[number];

export const ROLE_LABEL: Record<string, string> = {
  principal: 'Principal',
  principal_admin: 'Administrator',
  controller: 'Controller',
  property_manager: 'Property manager',
  devops_admin: 'Operations',
};

const everyone: readonly Role[] = ['principal', 'principal_admin', 'controller', 'property_manager', 'devops_admin'];

export const PERMISSIONS = {
  'chat.use': everyone,
  'documents.read': everyone,
  'documents.write': ['principal', 'principal_admin', 'controller', 'devops_admin'],
  'instructions.read': ['principal_admin', 'devops_admin'],
  'instructions.write': ['principal_admin', 'devops_admin'],
  'feedback.write': everyone,
  'review.read': ['devops_admin'],
  'conflicts.read': everyone,
  'conflicts.resolve': everyone,
  'conflicts.raise': ['devops_admin'],
  'period.close': ['controller', 'devops_admin'],
  'pipeline.run': ['controller', 'devops_admin'],
  'leases.read': everyone,
} as const satisfies Record<string, readonly Role[]>;

export type Capability = keyof typeof PERMISSIONS;

/** Roles are additive: the union of every role's capabilities. */
export function capabilitiesFor(roles: readonly string[] | undefined): ReadonlySet<Capability> {
  const held = new Set<Capability>();
  for (const [cap, allowed] of Object.entries(PERMISSIONS) as [Capability, readonly string[]][]) {
    if ((roles ?? []).some((r) => allowed.includes(r))) held.add(cap);
  }
  return held;
}

export function can(caps: ReadonlySet<Capability>, cap: Capability): boolean {
  return caps.has(cap);
}

/** Tooltip for a control the user can see but not use (rendered disabled, not hidden). */
export function missingReason(cap: Capability): string {
  const who = PERMISSIONS[cap].map((r) => ROLE_LABEL[r]).join(', ');
  return `Needs one of: ${who}`;
}
